/**
 * Side-anchored bug-report drawer. Replaces the previous bottom-sheet
 * popup so the user can keep navigating / typing in the rest of the app
 * while filling out a report. Built on `SideDrawer` (no scrim, no
 * click-blocking).
 *
 * Header has a "📋 Open my reports →" link that navigates to the
 * dedicated reports page (App.js view 'bug-reports'). Reports are no
 * longer browsable inside the drawer — that flow lives on its own page.
 *
 * Form features:
 *  - Tag pills (multi-select)
 *  - Title + description
 *  - Importance slider (feature/suggestion only)
 *  - Element picker via Shift+click (regular clicks still navigate)
 *  - Screenshot paste / drop / file picker → uploads to _storage
 *  - "Log steps to replicate" recording mode: drawer minimizes to a
 *    floating bar; Enter or "Done!" finalizes; captured audit events +
 *    breadcrumbs since recording started are merged into the report.
 *  - Otherwise, slider 1-20 to include the last N audit events.
 *
 * Capture (auto-attached, invisible to user):
 *  - lastAuditEvents (recorded slice OR last-N slider value)
 *  - breadcrumbs (recorded slice OR all current breadcrumbs)
 *  - characterSnapshot (slim)
 *  - appContext: route, viewport, role, char id, app commit
 *  - elements (from Shift+click picker)
 */

import React from 'react';
import SideDrawer from './SideDrawer.jsx';
import ScreenshotPasteZone from './ScreenshotPasteZone.jsx';
import ElementPicker from './ElementPicker.jsx';
import { useSubmitBugReport } from '../hooks/useBugReports.js';
import { generateReportId } from '../bugReportClient.js';
import { useUiBreadcrumb, pushBreadcrumb, readBreadcrumbsSnapshot } from '../hooks/useUiBreadcrumb.js';
import { useLastAuditEvents, matchesReporter } from '../hooks/useLastAuditEvents.js';

// Category palette + ordering. Updated 2026-04-30:
//   - 'feature' renamed to 'feedback' (read-time backwards-compat in
//     normalizeTag — no DB migration needed since no `feature` reports
//     exist in prod). Feedback is now last in the list per UX request.
//   - Colors retuned for the dev list-view: critical=red, bug=yellow,
//     interface=purple, suggestion=pastel blue, feedback=pastel pink,
//     question=pastel purple. The colors flow through TAG_COLORS in
//     the dev/my-reports pages too.
//   - Each option carries a one-line description shown inline under
//     the pill row when selected, so users learn what the categories
//     mean without leaving the form.
const TAG_OPTIONS = [
  { id: 'bug',        label: '🪲 Bug',         color: '#f5c842', description: 'Something broken or not working as expected.' },
  { id: 'critical',   label: '⚠️ Critical',    color: '#ff3b30', description: 'Blocking, severe, or game-breaking.' },
  { id: 'interface',  label: '🎛️ Interface',   color: '#9b59b6', description: 'UI / layout / styling issue or polish.' },
  { id: 'suggestion', label: '✨ Suggestion',  color: '#7eb6e8', description: 'An idea for a new feature or improvement.' },
  { id: 'question',   label: '❓ Question',    color: '#b89bdb', description: 'Asking how something works or is intended.' },
  { id: 'feedback',   label: '💬 Feedback',    color: '#e8a5c4', description: 'Things you like / dislike about the app or a specific feature.' },
];

// Categories that actually count toward the 2-tag limit. `critical` is
// orthogonal severity — it can stack on top of any other tag without
// using up a slot.
const CATEGORY_TAGS = new Set(['bug', 'interface', 'suggestion', 'question', 'feedback']);

// Mutually-exclusive pairs (order doesn't matter — checked both ways).
// Bug describes broken behavior; question/feedback describe intent or
// preference. Mixing them confuses both the dev and the reporter.
const MUTEX_PAIRS = [
  ['bug', 'question'],
  ['bug', 'feedback'],
];

// Backwards-compat tag normalization: legacy `feature` reads as
// `feedback`. Used in the dashboard / list views; the compose form
// emits the new tag name directly.
export const normalizeTag = (tag) => (tag === 'feature' ? 'feedback' : tag);

const APP_COMMIT_SHA = (process.env.REACT_APP_COMMIT_SHA || process.env.REACT_APP_GIT_SHA || 'dev');

// Criticality color ramp for the bug-mode pills. Index = level - 1.
//   1, 2 → green (minor)
//   3    → yellow (moderate)
//   4    → orange (high)
//   5    → red (blocking)
const CRITICALITY_COLORS = ['#27c95a', '#27c95a', '#ffd700', '#ff9500', '#ff3b30'];

// Pull audit events newer than `since` from the audit object and sort
// chronologically. Used by the recording flow on stop. Filters to the
// reporter's own actions — see matchesReporter in useLastAuditEvents.js
// for the reasoning (multi-user audit log, don't capture other users'
// actions in someone else's repro steps).
const sliceEventsSince = (audit, since, reporter) => {
  if (!audit?.events || !since) return [];
  return Object.values(audit.events)
    .filter(e => e && typeof e.ts === 'number' && e.ts >= since && matchesReporter(e, reporter))
    .map(e => ({
      ts: e.ts,
      category: e.category,
      action: e.action,
      actor: e.actor,
      target: e.target,
      summary: e.summary,
      importance: e.importance,
    }))
    .sort((a, b) => a.ts - b.ts);
};

// Try to find a human label adjacent to a form control. Walks `<label
// for=id>`, then a wrapping `<label>` ancestor, then a near-by sibling
// element with text content (like a div directly preceding the input
// that names it). All three patterns are common in the codebase.
const findAssociatedLabel = (el) => {
  if (!el) return null;
  // <label for="id">
  if (el.id && document.querySelector) {
    try {
      const lbl = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (lbl) {
        const text = (lbl.textContent || '').replace(/\s+/g, ' ').trim();
        if (text) return text.slice(0, 60);
      }
    } catch (_) { /* CSS.escape can throw on truly weird ids */ }
  }
  // <label> wrapping the element
  let cur = el.parentElement;
  for (let i = 0; i < 4 && cur; i++) {
    if (cur.tagName === 'LABEL') {
      const text = (cur.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text.slice(0, 60);
    }
    cur = cur.parentElement;
  }
  // Preceding sibling with text — common pattern: <div>label</div><input>
  const prev = el.previousElementSibling;
  if (prev) {
    const text = (prev.textContent || '').replace(/\s+/g, ' ').trim();
    // Only accept short text (label-like). Long text is probably content,
    // not a label.
    if (text && text.length <= 50) return text.slice(0, 60);
  }
  return null;
};

// Slim summary of a click target. For form controls, prefers semantic
// hints (label, aria-label, placeholder, name) over textContent — the
// textContent of a <select> is all options concatenated, which is noise.
const summarizeClickTarget = (el) => {
  if (!el) return null;
  const tag = (el.tagName || '').toLowerCase();
  const region = el.getAttribute && el.getAttribute('data-feedback-region');
  const testid = el.getAttribute && (el.getAttribute('data-testid') || el.getAttribute('data-test'));
  const id = el.id || null;
  const role = el.getAttribute && el.getAttribute('role');
  const ariaLabel = el.getAttribute && el.getAttribute('aria-label');
  const title = el.getAttribute && el.getAttribute('title');
  const isFormControl = tag === 'input' || tag === 'textarea' || tag === 'select';
  const placeholder = isFormControl ? (el.placeholder || null) : null;
  const name = isFormControl ? (el.name || null) : null;
  const inputType = (tag === 'input' && el.type) ? el.type : null;
  const labelText = isFormControl ? findAssociatedLabel(el) : null;

  // Best-available human label. For form controls, skip textContent — it's
  // either empty (input/textarea) or all options concatenated (select).
  let bestText = null;
  if (ariaLabel) bestText = ariaLabel;
  else if (title) bestText = title;
  else if (labelText) bestText = labelText;
  else if (placeholder) bestText = placeholder;
  else if (!isFormControl) {
    const tc = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (tc) bestText = tc.slice(0, 80);
  }
  if (!bestText && name) bestText = `(name=${name})`;

  return {
    tag,
    text: bestText || null,
    regionTag: region || null,
    testId: testid || null,
    elementId: id,
    role: role || null,
    ariaLabel: ariaLabel || null,
    titleAttr: title || null,
    placeholder,
    name,
    inputType,
    labelText,
  };
};

function ComposeForm({
  context,
  onSelectElement,
  onStartRecording,
  recordedSteps,
  onClearRecording,
  pickedElements,
  setPickedElements,
}) {
  const submitBugReport = useSubmitBugReport();
  const breadcrumbs = useUiBreadcrumb();
  // Auto-attach the last N audit events as loose context. The slider is
  // gone — testers ignored it and the preview confused readers — so we
  // just attach a fixed number behind the scenes. The recorded-steps
  // path takes priority when used.
  const AUTO_ATTACH_COUNT = 5;
  // Default tag = 'bug'. The form usually starts as a bug report; user
  // can deselect to file something else (feature/suggestion/etc.).
  const [tags, setTags] = React.useState(['bug']);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  // Importance value. Interpretation depends on the active scale:
  //   - critical tag → no UI, value not user-set (server stores null)
  //   - bug tag      → 1-5 ("Criticality")
  //   - else         → 1-10 ("How important is this to you?")
  // We store separate raw + scale + normalized fields on submit so the
  // dev-page sort can compare across scales. Default 3 — a sensible
  // mid-point in either scale.
  const [importance, setImportance] = React.useState(3);
  const [screenshots, setScreenshots] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(null);
  const [error, setError] = React.useState(null);
  const slicedEvents = useLastAuditEvents(
    context?.audit,
    AUTO_ATTACH_COUNT,
    undefined,
    { isDM: !!context?.isDM, charId: context?.charId || null },
  );

  // Importance mode (precedence): critical > bug > generic.
  const isCritical = tags.includes('critical');
  const isBug = !isCritical && tags.includes('bug');
  const importanceMode = isCritical ? 'critical' : isBug ? 'bug' : 'generic';
  const importanceMax = importanceMode === 'bug' ? 5 : 10;

  // If the user switches modes (e.g. removes bug, leaves only feature),
  // re-clamp the value into the new scale so we don't leave 7-of-5 lying
  // around. Critical mode hides the UI; we leave value unchanged in case
  // the user toggles critical off and wants their previous setting back.
  React.useEffect(() => {
    if (importanceMode === 'critical') return;
    setImportance(prev => Math.min(prev, importanceMax));
  }, [importanceMode, importanceMax]);

  const reset = () => {
    setTags(['bug']);
    setTitle('');
    setDescription('');
    setImportance(3);
    setScreenshots([]);
    setPickedElements([]);
    onClearRecording?.();
    setError(null);
  };

  // Tag selection rules (2026-04-30):
  //   - At most 2 categories selected at once. `critical` is severity,
  //     not category, so it doesn't count toward the limit.
  //   - Mutex pairs (MUTEX_PAIRS): bug↔question, bug↔feedback. Selecting
  //     one removes the other.
  //   - When the user adds a 3rd category, the OLDEST category is
  //     dropped (FIFO), so they never have to manually deselect to add.
  const toggleTag = (id) => {
    setTags(prev => {
      if (prev.includes(id)) return prev.filter(t => t !== id);
      let next = [...prev, id];
      // Apply mutex: drop anything that's mutually exclusive with `id`.
      for (const [a, b] of MUTEX_PAIRS) {
        if (id === a) next = next.filter(t => t !== b);
        else if (id === b) next = next.filter(t => t !== a);
      }
      // Enforce 2-category limit (critical excluded). Drop the oldest
      // category if we'd exceed it.
      const cats = next.filter(t => CATEGORY_TAGS.has(t));
      if (cats.length > 2) {
        const dropId = cats[0];
        next = next.filter(t => t !== dropId);
      }
      return next;
    });
  };

  const onSubmit = async () => {
    if (!title.trim() && !description.trim()) {
      setError('Add at least a title or description.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const reportId = generateReportId();
      // If a recording was made, prefer its captured events + breadcrumbs.
      // Otherwise use the slider's last-N audit events + the full breadcrumb buffer.
      const useRecorded = !!recordedSteps;
      // Importance shape: store the user's raw value + the scale they used,
      // plus a normalized 1-10 importance for cross-report sort. Critical
      // reports skip importance entirely — the tag itself signals max.
      let importancePayload;
      if (importanceMode === 'critical') {
        importancePayload = { importance: null, importanceRaw: null, importanceScale: null };
      } else if (importanceMode === 'bug') {
        importancePayload = {
          importance: importance * 2,   // 1-5 → 2-10
          importanceRaw: importance,
          importanceScale: 5,
        };
      } else {
        importancePayload = {
          importance,
          importanceRaw: importance,
          importanceScale: 10,
        };
      }
      const data = {
        title: title.trim(),
        description: description.trim(),
        tags,
        ...importancePayload,
        elements: pickedElements,
        lastAuditEvents: useRecorded ? recordedSteps.events : slicedEvents,
        // Marker for the agent reading this report: when the events come
        // from the auto-attached slice (no recording), they're loose
        // context — the most recent things in the session, not necessarily
        // related to the bug. Treat with grain of salt unless the report
        // text or elements explicitly reference them. When useRecorded is
        // true, the events are the actual repro path the user just took.
        lastAuditEventsAreRecorded: !!useRecorded,
        breadcrumbs: useRecorded ? recordedSteps.breadcrumbs : breadcrumbs.slice(),
        recordedInteractions: useRecorded ? (recordedSteps.interactions || []) : null,
        recordedSteps: useRecorded ? { startedAt: recordedSteps.startedAt, endedAt: recordedSteps.endedAt } : null,
        characterSnapshot: context?.characterSnapshot || null,
        appContext: {
          commitSha: APP_COMMIT_SHA,
          viewport: typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          view: context?.view || null,
          isDM: !!context?.isDM,
          charId: context?.charId || null,
          route: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
        },
        statusHistory: [],
      };
      await submitBugReport({
        reportId,
        data: JSON.stringify(data),
        screenshotStorageIds: screenshots.map(s => s.storageId),
      });
      setSubmitted({ reportId });
      reset();
    } catch (e) {
      console.error('Bug report submit failed:', e);
      setError(e?.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const removePickedElement = (idx) => {
    setPickedElements(prev => prev.filter((_, i) => i !== idx));
  };

  if (submitted) {
    return (
      <div style={{
        padding: 18,
        background: 'rgba(39,201,90,0.1)',
        border: '1px solid rgba(39,201,90,0.3)',
        borderRadius: 8,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '1rem', color: '#27c95a', fontWeight: 600, marginBottom: 8 }}>
          ✅ Thanks for the report!
        </div>
        <div style={{ fontSize: '0.78rem', color: '#b0a48a', marginBottom: 12 }}>
          Submitted as <code style={{ color: '#c9a227' }}>{submitted.reportId}</code>
        </div>
        <button
          onClick={() => setSubmitted(null)}
          style={{
            padding: '6px 14px', fontSize: '0.78rem',
            background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.4)',
            borderRadius: 6, color: '#c9a227', cursor: 'pointer',
          }}
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Tags */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>Tags</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {TAG_OPTIONS.map(t => {
            const selected = tags.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleTag(t.id)}
                style={{
                  padding: '4px 10px', fontSize: '0.7rem', borderRadius: 12,
                  border: `1px solid ${selected ? t.color : 'rgba(255,255,255,0.12)'}`,
                  background: selected ? `${t.color}22` : 'rgba(0,0,0,0.2)',
                  color: selected ? t.color : '#888',
                  cursor: 'pointer', fontWeight: selected ? 600 : 400,
                }}
                title={t.description}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Inline descriptions for selected tags. Teaches the categories
            without leaving the form, and confirms what was clicked. */}
        {tags.length > 0 && (
          <div style={{
            marginTop: 6,
            padding: '6px 8px',
            background: 'rgba(0,0,0,0.18)',
            border: '1px solid rgba(255,255,255,0.04)',
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
            {tags.map(id => {
              const opt = TAG_OPTIONS.find(o => o.id === id);
              if (!opt) return null;
              return (
                <div key={id} style={{ fontSize: '0.62rem', color: '#aaa', lineHeight: 1.4 }}>
                  <span style={{ color: opt.color, fontWeight: 600 }}>{opt.label}</span>
                  <span style={{ color: '#888' }}> — {opt.description}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Title */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>Title</div>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="One-line summary…"
          style={{
            width: '100%', padding: '6px 10px', fontSize: '0.85rem',
            background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(201,162,39,0.25)',
            borderRadius: 6, color: '#e8d5b7', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Description */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>What happened? (or what would you like?)</div>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe the bug, idea, or question…"
          rows={4}
          style={{
            width: '100%', padding: '6px 10px', fontSize: '0.85rem',
            background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(201,162,39,0.25)',
            borderRadius: 6, color: '#e8d5b7', outline: 'none',
            resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Importance — clickable square pills. Hidden when 'critical' tag
          is selected (the tag itself implies max). bug → 1-5 criticality
          with red-ramp colors; everything else → 1-10 user-importance. */}
      {importanceMode !== 'critical' && (
        <div style={{ marginBottom: 12 }}>
          {importanceMode === 'bug' ? (
            <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>
              <span style={{
                color: CRITICALITY_COLORS[importance - 1] || '#c9a227',
                fontWeight: 600,
              }}>
                ⚠️ Criticality
              </span>{' '}
              <span style={{ color: CRITICALITY_COLORS[importance - 1] || '#c9a227', fontWeight: 600 }}>
                {importance}/5
              </span>
            </div>
          ) : (
            <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>
              How important is this to you? <span style={{ color: '#c9a227', fontWeight: 600 }}>{importance}/10</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {Array.from({ length: importanceMax }, (_, i) => i + 1).map(n => {
              const selected = importance === n;
              const pillColor = importanceMode === 'bug' ? CRITICALITY_COLORS[n - 1] : '#c9a227';
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setImportance(n)}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 5,
                    border: `1px solid ${selected ? pillColor : 'rgba(255,255,255,0.12)'}`,
                    background: selected ? `${pillColor}33` : 'rgba(0,0,0,0.3)',
                    color: selected ? pillColor : '#888',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: selected ? 700 : 500,
                  }}
                >{n}</button>
              );
            })}
          </div>
          <div style={{ fontSize: '0.6rem', color: '#666', marginTop: 4 }}>
            {importanceMode === 'bug'
              ? '1 = minor, 5 = blocking. Sets criticality for this bug.'
              : `1 = nice-to-have, ${importanceMax} = most important.`}
          </div>
        </div>
      )}
      {importanceMode === 'critical' && (
        <div style={{
          marginBottom: 12,
          padding: '6px 10px',
          background: 'rgba(255,59,48,0.08)',
          border: '1px solid rgba(255,59,48,0.35)',
          borderRadius: 5,
          fontSize: '0.72rem',
          color: '#ff6961',
          fontWeight: 600,
        }}>
          ⚠️ Marked as critical
        </div>
      )}

      {/* Element picker */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.7rem', color: '#888' }}>Where on screen?</span>
          <button
            type="button"
            onClick={onSelectElement}
            style={{
              padding: '3px 10px', fontSize: '0.65rem',
              background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.4)',
              borderRadius: 4, color: '#c9a227', cursor: 'pointer',
            }}
          >
            🎯 Select element (Shift+click)
          </button>
          {pickedElements.length > 0 && (
            <button
              type="button"
              onClick={() => setPickedElements([])}
              title="Remove all selected elements"
              style={{
                padding: '3px 10px', fontSize: '0.62rem',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4, color: '#888', cursor: 'pointer',
              }}
            >
              Clear all
            </button>
          )}
        </div>
        {pickedElements.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {pickedElements.map((el, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 6px 2px 8px', fontSize: '0.62rem',
                  background: 'rgba(74,144,217,0.12)', border: '1px solid rgba(74,144,217,0.4)',
                  borderRadius: 10, color: '#4a90d9',
                }}
              >
                {el.regionTag || el.testId || el.elementId || el.cssPath || '(element)'}
                <button
                  onClick={() => removePickedElement(i)}
                  style={{ background: 'none', border: 'none', color: '#4a90d9', cursor: 'pointer', padding: 0, fontSize: '0.7rem' }}
                  title="Remove"
                >×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Screenshots */}
      <ScreenshotPasteZone items={screenshots} onChange={setScreenshots} />

      {/* Context capture. Banner with the recording CTA + explanation
          text (no slider — testers ignored it and the preview confused
          the agent). Last few audit events auto-attach behind the
          scenes; the agent receives them with a `lastAuditEventsAreRecorded:false`
          marker so it treats them as loose context. */}
      <div style={{
        marginBottom: 10,
        padding: 8,
        background: recordedSteps ? 'rgba(39,201,90,0.06)' : 'rgba(0,0,0,0.18)',
        border: `1px solid ${recordedSteps ? 'rgba(39,201,90,0.3)' : 'transparent'}`,
        borderRadius: 6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: '0.74rem', color: '#c9a227', fontWeight: 600 }}>
            {recordedSteps ? '✅ Steps recorded' : '📋 Action context'}
          </span>
          {recordedSteps ? (
            <button
              type="button"
              onClick={onClearRecording}
              style={{
                padding: '2px 8px', fontSize: '0.62rem',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4, color: '#888', cursor: 'pointer',
              }}
            >
              Clear & re-record
            </button>
          ) : (
            <button
              type="button"
              onClick={onStartRecording}
              style={{
                padding: '2px 10px', fontSize: '0.65rem', fontWeight: 600,
                background: 'rgba(74,144,217,0.18)',
                border: '1px solid #4a90d9',
                borderRadius: 4, color: '#7eb6e8', cursor: 'pointer',
              }}
            >
              🔁 Log steps to replicate
            </button>
          )}
        </div>

        {recordedSteps ? (
          <div style={{ fontSize: '0.7rem', color: '#b0a48a' }}>
            Captured{' '}
            <strong>{(recordedSteps.interactions || []).length}</strong> click{(recordedSteps.interactions || []).length === 1 ? '' : 's'},{' '}
            <strong>{recordedSteps.events.length}</strong> action{recordedSteps.events.length === 1 ? '' : 's'},{' '}
            and <strong>{recordedSteps.breadcrumbs.length}</strong> view change{recordedSteps.breadcrumbs.length === 1 ? '' : 's'}{' '}
            over {(Math.round((recordedSteps.endedAt - recordedSteps.startedAt) / 100) / 10).toFixed(1)}s. Will be attached to the report.
            {(recordedSteps.interactions || []).length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', color: '#888', fontSize: '0.65rem' }}>Preview steps</summary>
                <ol style={{ margin: '6px 0 0 0', paddingLeft: 22, maxHeight: 160, overflowY: 'auto', fontSize: '0.62rem' }}>
                  {recordedSteps.interactions.map((it, i) => {
                    const el = it.element || {};
                    const label =
                      el.regionTag ||
                      el.ariaLabel ||
                      el.titleAttr ||
                      (el.text && el.text.length > 0 ? `"${el.text}"` : null) ||
                      el.testId ||
                      el.elementId ||
                      `<${el.tag || 'element'}>`;
                    return (
                      <li key={i} style={{ color: '#b0a48a' }}>
                        <span style={{ color: '#666', fontFamily: 'monospace' }}>
                          {new Date(it.ts).toLocaleTimeString()}
                        </span>{' '}
                        click <strong style={{ color: '#e8d5b7' }}>{label}</strong>{' '}
                        <span style={{ color: '#666' }}>({el.tag || 'el'})</span>
                      </li>
                    );
                  })}
                </ol>
              </details>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '0.65rem', color: '#888', lineHeight: 1.5 }}>
            Click the button, redo what triggered the bug, then press{' '}
            <strong style={{ color: '#c9a227' }}>Enter</strong> or click{' '}
            <strong style={{ color: '#c9a227' }}>Done</strong> on the recording bar.
            The drawer minimizes while recording so you can use the app normally.
          </div>
        )}
      </div>

      {/* Breadcrumbs preview (always available; less prominent) */}
      {breadcrumbs.length > 0 && !recordedSteps && (
        <details style={{ marginBottom: 10, fontSize: '0.7rem' }}>
          <summary style={{ cursor: 'pointer', color: '#888' }}>
            🧭 UI navigation history ({breadcrumbs.length})
          </summary>
          <div style={{
            padding: 8, background: 'rgba(0,0,0,0.18)', borderRadius: 6,
            marginTop: 6, maxHeight: 110, overflowY: 'auto',
            fontSize: '0.62rem', color: '#b0a48a',
          }}>
            {breadcrumbs.slice().reverse().map((b, i) => (
              <div key={i}>
                <span style={{ color: '#666' }}>{new Date(b.ts).toLocaleTimeString()}</span>{' '}
                <span style={{ color: '#c9a227' }}>{b.type}</span>: {b.value}
              </div>
            ))}
          </div>
        </details>
      )}

      {error && (
        <div style={{ fontSize: '0.7rem', color: '#ff6961', marginBottom: 8 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          onClick={onSubmit}
          disabled={submitting}
          style={{
            flex: 1, padding: '8px 14px', fontSize: '0.85rem', fontWeight: 600,
            background: submitting ? 'rgba(201,162,39,0.05)' : 'rgba(39,201,90,0.15)',
            border: `1px solid ${submitting ? 'rgba(201,162,39,0.2)' : '#27c95a50'}`,
            borderRadius: 6, color: submitting ? '#888' : '#27c95a',
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>

    </>
  );
}

export default function QuickReportOverlay({
  open,
  onClose,
  context,
  onNavigateToReports,
  onNavigateToDev,
  onCloseOtherSurfaces,
  isDev,
}) {
  const [pickerActive, setPickerActive] = React.useState(false);
  const [pickedElements, setPickedElements] = React.useState([]);
  // Recording state. When set, the drawer minimizes to a floating bar
  // so the user can perform the steps that reproduce the bug. While
  // active, a window-level click listener captures every interaction
  // (skipping clicks inside our own UI). On stop, we merge the captured
  // clicks with audit events + breadcrumbs from the same window.
  const [recording, setRecording] = React.useState(null);   // { startedAt }
  const [recordedSteps, setRecordedSteps] = React.useState(null);
  // Mutable buffer for captured clicks during recording. Lives across
  // renders without re-mounting the listener.
  const recordingBufferRef = React.useRef([]);

  // Lifecycle breadcrumbs for the drawer itself.
  React.useEffect(() => {
    if (!open) return;
    pushBreadcrumb('modal', 'bug-report-drawer', { action: 'open' });
    return () => pushBreadcrumb('modal', 'bug-report-drawer', { action: 'close' });
  }, [open]);

  const startRecording = () => {
    // Close other floating surfaces so they don't cover the recording bar
    // or pollute the captured timeline. The user reopens what they need
    // as part of the steps they're recording — that's the actual repro
    // path the dev wants to see.
    onCloseOtherSurfaces?.();
    setRecordedSteps(null);
    recordingBufferRef.current = [];
    setRecording({ startedAt: Date.now() });
  };

  const stopRecording = React.useCallback(() => {
    if (!recording) return;
    const endedAt = Date.now();
    const events = sliceEventsSince(
      context?.audit,
      recording.startedAt,
      { isDM: !!context?.isDM, charId: context?.charId || null },
    );
    const breadcrumbs = readBreadcrumbsSnapshot().filter(b => b.ts >= recording.startedAt);
    const interactions = recordingBufferRef.current.slice();
    // Empty capture — user pressed Enter immediately, or didn't perform
    // any captureable actions during the recording window. Treat this
    // as an implicit cancel: revert to the slider-based last-N flow
    // rather than save an empty "recorded steps" payload that's worse
    // than no data at all.
    if (events.length === 0 && breadcrumbs.length === 0 && interactions.length === 0) {
      recordingBufferRef.current = [];
      setRecording(null);
      setRecordedSteps(null);
      return;
    }
    setRecordedSteps({
      events,
      breadcrumbs,
      interactions,
      startedAt: recording.startedAt,
      endedAt,
    });
    recordingBufferRef.current = [];
    setRecording(null);
  }, [recording, context?.audit, context?.isDM, context?.charId]);

  // While recording, listen for every click on the page and stash a slim
  // summary into the buffer. Skip clicks inside our own UI (the drawer,
  // the floating bug button, the picker overlay) — those would be noise.
  // Capture-phase listener so we see clicks before any inline handler
  // calls stopPropagation. We never preventDefault / stopPropagation
  // ourselves — the click flows through normally.
  React.useEffect(() => {
    if (!recording) return undefined;
    const onClick = (e) => {
      const target = e.target;
      if (!target || !target.closest) return;
      if (
        target.closest('[data-bug-report-drawer]')
        || target.closest('[data-bug-report-button]')
        || target.closest('[data-feedback-picker-ui]')
      ) return;
      recordingBufferRef.current.push({
        ts: Date.now(),
        kind: 'click',
        element: summarizeClickTarget(target),
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [recording]);

  // While the picker is active, Enter (outside an input) commits the
  // current picks and exits picker mode — same UX as the recording
  // "press Enter to finish" flow. Picks are already in pickedElements;
  // we just need to flip pickerActive off.
  React.useEffect(() => {
    if (!pickerActive) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Enter') return;
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t?.tagName || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      e.preventDefault();
      setPickerActive(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pickerActive]);

  // Listen for Enter to finish recording. Only when recording is active.
  // Skips when the user is typing in an input/textarea/contenteditable —
  // they're naming an item or editing a journal entry, not finishing.
  React.useEffect(() => {
    if (!recording) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Enter') return;
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t?.tagName || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      e.preventDefault();
      stopRecording();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [recording, stopRecording]);

  const handleClose = () => {
    // Don't close while recording / picking — the floating bar IS the
    // drawer's collapsed form. Finish first.
    if (recording) {
      stopRecording();
      return;
    }
    if (pickerActive) {
      setPickerActive(false);
      return;
    }
    onClose?.();
  };

  // Drawer's minimized state is shared between recording and picking.
  // The summary text + click-to-finish behavior diverge; this picks the
  // right pair based on which mode is active.
  const drawerMinimized = !!recording || pickerActive;
  const drawerMinimizedSummary = recording
    ? `Recording steps… Press Enter or click here to finish.`
    : pickerActive
    ? `🎯 Shift+click to add (${pickedElements.length} selected). Enter or click here to finish.`
    : null;
  const onDrawerExpandFromMinimized = recording
    ? stopRecording
    : pickerActive
    ? () => setPickerActive(false)
    : undefined;

  return (
    <>
      <SideDrawer
        open={open}
        onClose={handleClose}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>🪲 Report Issue / Idea</span>
            {onNavigateToReports && (
              <button
                onClick={() => { onClose?.(); onNavigateToReports(); }}
                style={{
                  padding: '3px 10px',
                  fontSize: '0.72rem',
                  background: 'rgba(74,144,217,0.12)',
                  border: '1px solid rgba(74,144,217,0.4)',
                  borderRadius: 4,
                  color: '#4a90d9',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                📋 My reports →
              </button>
            )}
            {isDev && onNavigateToDev && (
              <button
                onClick={() => { onClose?.(); onNavigateToDev(); }}
                title="Dev tools — all reports, filters, export"
                style={{
                  padding: '3px 10px',
                  fontSize: '0.72rem',
                  background: 'rgba(155,89,182,0.12)',
                  border: '1px solid rgba(155,89,182,0.4)',
                  borderRadius: 4,
                  color: '#9b59b6',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                🛠️ Dev →
              </button>
            )}
          </span>
        }
        minimized={drawerMinimized}
        onExpandFromMinimized={onDrawerExpandFromMinimized}
        minimizedSummary={drawerMinimizedSummary}
      >
        <ComposeForm
          context={context}
          onSelectElement={() => {
            // Same rationale as startRecording: close other surfaces so
            // the picker's hover overlay isn't fighting them for attention.
            onCloseOtherSurfaces?.();
            setPickerActive(true);
          }}
          onStartRecording={startRecording}
          recordedSteps={recordedSteps}
          onClearRecording={() => setRecordedSteps(null)}
          pickedElements={pickedElements}
          setPickedElements={setPickedElements}
        />
      </SideDrawer>

      {pickerActive && (
        <ElementPicker
          pickedElements={pickedElements}
          onTogglePick={(el) => setPickedElements(prev => {
            // Toggle: if cssPath / elementId matches an existing pick,
            // remove it; otherwise append. Mirrors the deselect UX of
            // shift-clicking the same element a second time.
            const idx = prev.findIndex(p => (
              (p.cssPath && el.cssPath && p.cssPath === el.cssPath)
              || (p.elementId && el.elementId && p.elementId === el.elementId)
            ));
            if (idx >= 0) return prev.filter((_, i) => i !== idx);
            return [...prev, el];
          })}
          onCancel={() => setPickerActive(false)}
        />
      )}
    </>
  );
}
